import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { CustomUser, UserPermission } from 'api';
import {
  FIRM_COMPANY_USER_PERMISSIONS_QUERY_KEY,
  FIRM_COMPANY_USER_PERMISSIONS_STALE_TIME,
} from 'common/constants/services/firms';
import { CompanyByFirm } from 'common/types/company';
import { User } from 'common/types/user';
import { getFirmUserPermissions } from 'services/hooks/useUserSelected';
import { getNumberValue } from 'utilities';
import { Logger } from 'utilities/logger';
import { CompanyUserPermission, FirmUserWithPermissions } from './useCompanyPermissions.types';

const permissionBelongsToCompany = (permission: UserPermission, companyId: number) =>
  permission.feature_object?.object_id === companyId && permission.feature_object?.object_type === 'Company';

const mapCompanyPermissions = (companyId: number, firmUserPermissions: FirmUserWithPermissions[]) =>
  firmUserPermissions.flatMap(user =>
    (user.user_permission_set ?? [])
      .filter(permission => permissionBelongsToCompany(permission, companyId))
      .map(permission => ({
        user_id: permission.user_id ? Number(permission.user_id) : getNumberValue(user.id),
      }))
  );

const toPermissionUser = (user: CustomUser | FirmUserWithPermissions): User => ({
  email: user.email,
  id: user.id,
  is_staff: user.is_staff,
  is_superuser: user.is_superuser,
  username: user.username,
  firms_permissions: [],
});

const buildUpdatedCompany = (
  company: CompanyByFirm,
  permissions: CompanyUserPermission[],
  firmUsers: CustomUser[],
  firmUserPermissions: FirmUserWithPermissions[]
) => {
  const permissionUserIds = new Set(permissions.map(permission => permission.user_id));
  const permissionUsersById = new Map<number, User>();

  [...firmUsers, ...firmUserPermissions].forEach(user => {
    if (user.id) {
      permissionUsersById.set(user.id, toPermissionUser(user));
    }
  });

  return {
    ...company,
    users: Array.from(permissionUserIds)
      .map(userId => permissionUsersById.get(userId))
      .filter((user): user is User => Boolean(user)),
    usersNotInCompany: firmUsers
      .filter(user => user.id && !permissionUserIds.has(user.id))
      .map(toPermissionUser),
  };
};

export const useCompanyPermissions = (firmId: number, filteredCompanies: CompanyByFirm[], firmUsers: CustomUser[]) => {
  const {
    data: firmUserPermissions = [],
    error,
    isError,
    isLoading,
  } = useQuery({
    enabled: Boolean(firmId) && filteredCompanies.length > 0,
    queryKey: [FIRM_COMPANY_USER_PERMISSIONS_QUERY_KEY, firmId],
    queryFn: () => getFirmUserPermissions(firmId) as Promise<FirmUserWithPermissions[]>,
    retry: false,
    staleTime: FIRM_COMPANY_USER_PERMISSIONS_STALE_TIME,
  });

  // React Query owns loading/cache here; this effect only surfaces a failed permission lookup.
  useEffect(() => {
    if (isError) {
      enqueueSnackbar(`Error fetching user permissions for firm ID ${firmId}`, { variant: 'error' });
      Logger.error(`Error fetching user permissions for firm ID ${firmId}`, error);
    }
  }, [error, firmId, isError]);

  const companiesWithPermissions = useMemo(() => {
    if (!firmId || filteredCompanies.length === 0 || isError) {
      return filteredCompanies;
    }

    const companyPermissionsMap = filteredCompanies.reduce((acc, company) => {
      acc[getNumberValue(company.company_id)] = mapCompanyPermissions(
        getNumberValue(company.company_id),
        firmUserPermissions
      );
      return acc;
    }, {} as Record<number, CompanyUserPermission[]>);

    return filteredCompanies.map(company =>
      buildUpdatedCompany(
        company,
        companyPermissionsMap[getNumberValue(company.company_id)] || [],
        firmUsers,
        firmUserPermissions
      )
    );
  }, [filteredCompanies, firmId, firmUsers, firmUserPermissions, isError]);

  return { companiesWithPermissions, loading: isLoading };
};
